/**
 * Materials Service
 *
 * Handles all material management operations including:
 * - Material CRUD operations
 * - Material price management
 * - Low stock alerts
 */

import httpClient from './httpClient';

const materialService = {
  // ==================== List & Search ====================

  /**
   * Get list of materials with filtering and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default 1)
   * @param {number} params.page_size - Items per page (default 20)
   * @param {string} params.search - Search term
   * @param {string} params.material_category - Filter by category (棒料, 紧固件, 耗材)
   * @param {boolean} params.is_active - Filter by active status
   */
  async getMaterials(params = {}) {
    try {
      const response = await httpClient.get('/api/materials', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching materials:', error);
      throw error;
    }
  },

  /**
   * Get a single material by ID
   * @param {string} materialId - Material ID
   */
  async getMaterial(materialId) {
    try {
      const response = await httpClient.get(`/api/materials/${materialId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching material:', error);
      throw error;
    }
  },

  // ==================== CRUD Operations ====================

  /**
   * Create a new material
   * @param {Object} materialData - Material data
   * @param {string} materialData.name - Material name (required)
   * @param {string} materialData.product_id - Unique product code (required)
   * @param {string} materialData.material_category - Category (棒料, 紧固件, 耗材)
   * @param {string} materialData.unit - Unit of measure
   * @param {number} materialData.diameter - Diameter (for bar stock)
   * @param {string} materialData.specification - Specification
   * @param {number} materialData.default_unit_cost - Default unit cost
   * @param {string} materialData.notes - Optional notes
   */
  async createMaterial(materialData) {
    try {
      const response = await httpClient.post('/api/materials', materialData);
      return response.data;
    } catch (error) {
      console.error('Error creating material:', error);
      throw error;
    }
  },

  /**
   * Update an existing material
   * @param {string} materialId - Material ID
   * @param {Object} updates - Fields to update
   */
  async updateMaterial(materialId, updates) {
    try {
      const response = await httpClient.put(`/api/materials/${materialId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating material:', error);
      throw error;
    }
  },

  /**
   * Delete (deactivate) a material
   * @param {string} materialId - Material ID
   */
  async deleteMaterial(materialId) {
    try {
      const response = await httpClient.delete(`/api/materials/${materialId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting material:', error);
      throw error;
    }
  },

  // ==================== Price Management ====================

  /**
   * Get price history for a material
   * @param {string} materialId - Material ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max records to return (default 50)
   * @param {string} params.supplier_id - Filter by supplier
   */
  async getMaterialPrices(materialId, params = {}) {
    try {
      const response = await httpClient.get(`/api/materials/${materialId}/prices`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching material prices:', error);
      throw error;
    }
  },

  /**
   * Add a new price for a material
   * @param {string} materialId - Material ID
   * @param {Object} priceData - Price data
   * @param {number} priceData.unit_price_tax_included - Price including tax (required)
   * @param {number} priceData.tax_rate - Tax rate (default 0.13)
   * @param {string} priceData.supplier_id - Supplier ID
   * @param {string} priceData.supplier_name - Supplier name
   * @param {string} priceData.effective_date - Price effective date
   * @param {string} priceData.notes - Optional notes
   */
  async addMaterialPrice(materialId, priceData) {
    try {
      const response = await httpClient.post(`/api/materials/${materialId}/prices`, priceData);
      return response.data;
    } catch (error) {
      console.error('Error adding material price:', error);
      throw error;
    }
  },

  // ==================== Material Prices API ====================

  /**
   * Get list of all material prices
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @param {string} params.search - Search term
   * @param {string} params.product_id - Filter by product
   * @param {string} params.supplier_id - Filter by supplier
   * @param {string} params.material_type - Filter by type (bar_stock, fastener, consumable)
   * @param {boolean} params.active_only - Only active prices (default true)
   */
  async getAllPrices(params = {}) {
    try {
      const response = await httpClient.get('/api/material-prices', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching material prices:', error);
      throw error;
    }
  },

  /**
   * Create a new material price
   * @param {Object} priceData - Price data
   */
  async createPrice(priceData) {
    try {
      const response = await httpClient.post('/api/material-prices', priceData);
      return response.data;
    } catch (error) {
      console.error('Error creating price:', error);
      throw error;
    }
  },

  /**
   * Update a material price
   * @param {string} priceId - Price ID
   * @param {Object} updates - Fields to update
   */
  async updatePrice(priceId, updates) {
    try {
      const response = await httpClient.put(`/api/material-prices/${priceId}`, updates);
      return response.data;
    } catch (error) {
      console.error('Error updating price:', error);
      throw error;
    }
  },

  /**
   * Delete a material price
   * @param {string} priceId - Price ID
   */
  async deletePrice(priceId) {
    try {
      const response = await httpClient.delete(`/api/material-prices/${priceId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting price:', error);
      throw error;
    }
  },

  /**
   * Lookup current price for a product
   * @param {string} productId - Product ID
   * @param {string} supplierId - Optional supplier filter
   */
  async lookupPrice(productId, supplierId = null) {
    try {
      const params = supplierId ? { supplier_id: supplierId } : {};
      const response = await httpClient.get(`/api/material-prices/lookup/${productId}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error looking up price:', error);
      throw error;
    }
  },

  /**
   * Get price by bar stock diameter
   * @param {number} diameter - Diameter in mm
   * @param {string} supplierId - Optional supplier filter
   */
  async getPriceByDiameter(diameter, supplierId = null) {
    try {
      const params = supplierId ? { supplier_id: supplierId } : {};
      const response = await httpClient.get(`/api/material-prices/by-diameter/${diameter}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error getting price by diameter:', error);
      throw error;
    }
  },

  // ==================== Low Stock Alerts ====================

  /**
   * Get materials with low stock levels
   * @param {number} threshold - Stock threshold (default 10)
   */
  async getLowStockMaterials(threshold = 10) {
    try {
      const response = await httpClient.get('/api/materials/low-stock', {
        params: { threshold }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching low stock materials:', error);
      throw error;
    }
  },

  // ==================== AI Price Import ====================

  /**
   * Extract prices from uploaded file using AI
   * @param {File} file - Excel or PDF file
   * @returns {Object} Extraction result with matched items
   */
  async extractPrices(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await httpClient.post('/api/material-prices/extract', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error extracting prices:', error);
      throw error;
    }
  },

  /**
   * Bulk update material prices from confirmed extraction
   * @param {Object} data - Update data
   * @param {Array} data.items - Items to update with product_id, unit_price, tax_rate
   * @param {string} data.supplier_id - Supplier ID
   * @param {string} data.supplier_name - Supplier name
   * @param {string} data.effective_date - Price effective date (YYYY-MM-DD)
   * @param {string} data.source_file - Source file name
   */
  async bulkUpdatePrices(data) {
    try {
      const response = await httpClient.post('/api/material-prices/bulk-update', data);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating prices:', error);
      throw error;
    }
  },

  /**
   * Get price import history
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.page_size - Items per page
   * @param {string} params.supplier_id - Filter by supplier
   */
  async getImportHistory(params = {}) {
    try {
      const response = await httpClient.get('/api/material-prices/import-history', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching import history:', error);
      throw error;
    }
  },

  /**
   * Get details of a specific import
   * @param {string} importId - Import ID
   */
  async getImportDetail(importId) {
    try {
      const response = await httpClient.get(`/api/material-prices/import-history/${importId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching import detail:', error);
      throw error;
    }
  },

  /**
   * Rollback a price import (admin only)
   * @param {string} importId - Import ID to rollback
   */
  async rollbackImport(importId) {
    try {
      const response = await httpClient.post(`/api/material-prices/import-history/${importId}/rollback`);
      return response.data;
    } catch (error) {
      console.error('Error rolling back import:', error);
      throw error;
    }
  },

  /**
   * Create multiple materials at once
   * @param {Array} materials - Array of material data objects
   * @returns {Object} Result with created materials
   */
  async createMultipleMaterials(materials) {
    try {
      const response = await httpClient.post('/api/materials/bulk-create', { materials });
      return response.data;
    } catch (error) {
      console.error('Error creating multiple materials:', error);
      throw error;
    }
  }
};

export default materialService;
